import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from './useRedux';
import { negocioService, ContextoNegocio, modificadoresService, GrupoModificador } from '@/api/restauranteService';

/** Módulos base: los que tiene toda empresa aunque el backend no responda. */
export const MODULOS_BASE = [
  'POS', 'VENTAS', 'CLIENTES', 'INVENTARIO', 'CATALOGO', 'COMPRAS', 'LEALTAD',
  'PROMOCIONES', 'FACTURACION', 'CAJA', 'REPORTES', 'CONFIGURACION', 'SEGURIDAD',
];

export const claveNegocio = (empresaId?: number) => ['negocio', empresaId ?? 0];

/**
 * Tipo de negocio y módulos activos de la empresa del usuario.
 * Mientras carga (o si el backend aún no tiene la migración) se comporta
 * como comercio general, que es exactamente lo que había antes.
 */
export function useNegocio() {
  const empresaId = useAppSelector(s => s.auth.user?.empresaId) ?? 0;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: claveNegocio(empresaId),
    queryFn: () => negocioService.contexto(empresaId),
    enabled: empresaId > 0,
    staleTime: 10 * 60 * 1000,
    retry: 0,
  });

  const ctx: ContextoNegocio | undefined = query.data;
  const modulos = ctx?.modulos ?? MODULOS_BASE;

  const tieneModulo = useCallback((codigo?: string) => !codigo || modulos.includes(codigo), [modulos]);

  const refrescar = useCallback(
    (nuevo?: ContextoNegocio) => nuevo
      ? qc.setQueryData(claveNegocio(empresaId), nuevo)
      : qc.invalidateQueries({ queryKey: claveNegocio(empresaId) }),
    [qc, empresaId]);

  return {
    empresaId,
    cargando: query.isLoading,
    tipoNegocio: ctx?.tipoNegocio ?? 'GENERAL',
    tipoNegocioNombre: ctx?.tipoNegocioNombre ?? 'Comercio general',
    pantallaInicio: ctx?.pantallaInicio ?? 'CAJA',
    modulos,
    contexto: ctx,
    restaurante: ctx?.restaurante ?? { propinaSugeridaPct: 10, propinaObligatoria: false, comandaAutomatica: true, descontarRecetas: true },
    tieneModulo,
    usaMesas: modulos.includes('MESAS'),
    usaCocina: modulos.includes('COCINA'),
    usaModificadores: modulos.includes('MODIFICADORES'),
    refrescar,
  };
}

/** Grupos de modificadores por producto (para el terminal). */
export function useModificadoresPorProducto(habilitado: boolean) {
  const empresaId = useAppSelector(s => s.auth.user?.empresaId) ?? 0;
  const q = useQuery({
    queryKey: ['modificadores-por-producto', empresaId],
    queryFn: () => modificadoresService.porProducto(empresaId),
    enabled: habilitado && empresaId > 0,
    staleTime: 2 * 60 * 1000,
    retry: 0,
  });
  const mapa: Record<string, GrupoModificador[]> = q.data ?? {};
  return {
    gruposDe: (productoId: number | string) => mapa[String(productoId)] ?? [],
    cargando: q.isLoading,
  };
}
